// liveBoardDraftLog.js — the Live Board's permanent record.
//
// A live board is a REAL auction. The share link is disposable — api/live-board
// expires it 14 days after the draft, and that TTL is not touched here — but
// the RESULTS are the most valuable data this site has: real rooms, real
// prices, real clearing behaviour, against which every calibration target in
// CLAUDE.md is ultimately graded. So the results are logged permanently,
// through the same endpoint and into the same "drafts" set the mock drafts use
// (api/draft-log.js), and dumpdrafts.mjs picks them up with no change.
//
// ══════════════════════════════════════════════════════════════════════
// 🔴 THE SAME ONE RULE AS liveBoardPublish.js: THIS MUST NEVER BLOCK, DELAY,
// OR THROW INTO THE ENTRY PATH.
//
// It runs on every commit, which is Ben's hands at the bottom edge of the
// screen for 160 picks with eleven people waiting. Telemetry that can cost a
// keystroke is worse than no telemetry. So, exactly as publishBoard does it:
//   - not async, nothing awaitable to return
//   - the whole body inside try/catch, and the fetch carries .catch()
//   - no retries; the NEXT entry flushes the whole board again, so one dropped
//     POST self-heals within one pick and a retry storm cannot form
//   - the caller has already written localStorage, unconditionally, first
// ══════════════════════════════════════════════════════════════════════
//
// ══════════════════════════════════════════════════════════════════════
// 🔴 WHAT IS IN THE RECORD, AND WHAT IS DELIBERATELY NOT.
//
// IN:  the room's SHAPE (budget, teams, positions), the team names, and one
//      event per sale — player name and position, the seat that won him, the
//      price, and when.
//
// NOT: any identity. No distinctId, no league name, no user seat, no share
//      URL. The setup screen tells the user in as many words that "an
//      anonymous record of the results (players, prices, team names, nothing
//      about you) is kept as part of the site's auction data", and this
//      function is what that sentence has to stay true of. A field added here
//      is a promise broken on a screen somebody already read.
//
// ⚠️ TEAM NAMES ARE IN, AND THEY ARE REAL PEOPLE'S NAMES. That is a deliberate
// call and it is the one the disclosure names explicitly: a roster without the
// seats it belongs to is not a reconstructable auction, and the names are
// already published to the share link. They identify a SEAT, not an account —
// there is nothing in the record to join "Mike" to a person.
// ══════════════════════════════════════════════════════════════════════

const ENDPOINT = '/api/draft-log';

/** A live board's stable draft id — derived, so every flush upserts one key. */
export function liveDraftId(shareId) {
  return `live-${shareId}`;
}

/**
 * The board as a draft-log record.
 *
 * Pure and exported so a test can read it without a network. `complete` is
 * passed in rather than computed here: the caller already knows (it holds
 * `boardIsComplete`), and importing that would pull the whole liveBoard module
 * into a file whose entire job is one fetch.
 */
export function liveBoardRecord(board, { complete = false } = {}) {
  const sales = Array.isArray(board?.sales) ? board.sales : [];
  return {
    draftId: liveDraftId(board?.shareId),
    source: 'live_board',
    // Two statuses only. "started" is every flush up to the last one; a board
    // whose every seat is full is "completed". There is no "abandoned" — a
    // real auction that stops early stopped for a reason we cannot see, and
    // the record of what DID sell is just as true either way.
    status: complete ? 'completed' : 'started',
    startedAt: Number.isFinite(board?.startedAt) ? board.startedAt : null,
    // The shape of the room, and nothing about who is in it.
    settings: {
      budget: board?.budget ?? null,
      teams: Array.isArray(board?.teamNames) ? board.teamNames.length : null,
      positions: board?.positions || null,
    },
    teamNames: Array.isArray(board?.teamNames) ? [...board.teamNames] : [],
    // One event per sale, in entry order, which IS nomination order on a live
    // board — the operator types them as they happen. `pick` is 1-based to
    // match the mock log's own convention.
    //
    // ⚠️ posRank is NOT carried, for the same reason it is not published: it
    // is our editorial rank layer, not a fact about the auction. The name and
    // the position are what a corpus needs; the rank of the day can be
    // re-derived from the board it was drafted against.
    events: sales.map((s, i) => ({
      type: 'sale',
      pick: i + 1,
      player: { name: s?.name ?? null, pos: s?.pos ?? null },
      winnerSeat: Number.isFinite(s?.team) ? s.team : null,
      price: Number.isFinite(s?.price) ? s.price : null,
      ts: Number.isFinite(s?.ts) ? s.ts : null,
    })),
  };
}

/**
 * Fire-and-forget. Returns nothing; callers must not await it.
 *
 * No-ops without a shareId — the id is what makes the flush an upsert rather
 * than a new record every pick, so a board without one must not be logged at
 * all.
 */
export function logLiveBoard(board, { complete = false } = {}) {
  if (!board || !board.shareId) return;
  try {
    const body = JSON.stringify(liveBoardRecord(board, { complete }));
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* the next entry re-flushes the whole board */ });
  } catch {
    // fetch missing, body unserializable, anything at all — the entry path
    // does not find out.
  }
}
